<template>
  <answer-button :session-id="sessionId">
    <template #default="{ answer, loading }">
      <wui-btn
        v-if="!showMenu"
        data-test="answer-button"
        :variant="variant"
        :state="buttonState"
        :size="size"
        icon
        auto-loading
        :disabled="loading || isLoading"
        :prepend-icon="resolveAcceptIcon(loading)"
        :class="buttonClass"
        :title="title || $t('common.acceptCall')"
        @click="onAnswer(answer, defaultStrategy)"
      >
        <span
          v-if="srOnlyLabel"
          class="sr-only select-none"
        >{{ srOnlyLabel }}</span>
      </wui-btn>
      <wui-dropdown
        v-else
        :direction="dropdownDirection"
        close-on-click-outside
        plain
      >
        <template #activator="{ toggle }">
          <wui-btn
            data-test="answer-button"
            :variant="variant"
            :state="buttonState"
            :size="size"
            icon
            auto-loading
            :disabled="loading || isLoading"
            :prepend-icon="resolveMenuIcon(loading)"
            :class="buttonClass"
            :title="$t('calls.acceptCallOptions')"
            @click="toggle()"
          >
            <span class="sr-only select-none">{{ $t('calls.acceptCallOptions') }}</span>
          </wui-btn>
        </template>
        <template #default="{ close }">
          <wui-card
            class="incoming-answer-actions-menu w-70 rounded-10!"
            :class="menuClass"
            state="text"
            shadow="overflow"
          >
            <wui-list class="incoming-answer-actions-menu__list p-1">
              <wui-list-item
                class="incoming-answer-actions-menu__item rounded-12! px-3 py-3 text-body-r-16-24"
                icon="phoneDownM"
                @click="onAnswer(answer, 'end-and-accept', close)"
              >
                {{ $t('calls.endCurrentAndAccept') }}
              </wui-list-item>
              <wui-list-item
                class="incoming-answer-actions-menu__item rounded-12! px-3 py-3 text-body-r-16-24"
                icon="pauseM"
                @click="onAnswer(answer, 'hold-and-accept', close)"
              >
                {{ $t('calls.holdCurrentAndAccept') }}
              </wui-list-item>
              <wui-list-item
                class="incoming-answer-actions-menu__item rounded-12! px-3 py-3 text-body-r-16-24"
                icon="pauseM"
                @click="onAnswer(answer, 'hold-incoming-and-accept', close)"
              >
                {{ $t('calls.holdIncomingAndAccept') }}
              </wui-list-item>
            </wui-list>
          </wui-card>
        </template>
      </wui-dropdown>
    </template>
  </answer-button>
</template>

<script setup lang="ts">
import { Direction, type DirectionType, type IconName, useRenderNotifier, WuiBtn, WuiCard, WuiDropdown, WuiList, WuiListItem } from '@wui/common-library'
import { computed, ref } from 'vue'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useWebRTC } from '@/entities/webrtc'

import { useLocalization } from '@/shared/composables/localization'

import {
  getConnectedOtherSessions,
  IncomingAnswerStrategy,
  runIncomingAnswerStrategy,
  shouldShowIncomingAnswerMenu,
} from '../lib/answer-incoming-strategies'

import AnswerButton from './answer-button.vue'

const props = withDefaults(defineProps<{
  sessionId: string
  withAudio?: boolean
  withVideo?: boolean
  hideAnswerMenu?: boolean
  variant?: 'positive' | 'negative' | 'neut' | 'brand'
  buttonState?: 'filled' | 'tonal' | 'alpha' | 'outline'
  size?: 24 | 32 | 40 | 48 | 72
  acceptIcon?: IconName
  menuIcon?: IconName
  buttonClass?: string
  menuClass?: string
  title?: string
  srOnlyLabel?: string
  dropdownDirection?: DirectionType
}>(), {
  withAudio: true,
  withVideo: false,
  hideAnswerMenu: false,
  variant: 'positive',
  buttonState: 'filled',
  size: 48,
  acceptIcon: 'phoneF',
  menuIcon: 'kebabVerticalM',
  buttonClass: '',
  menuClass: '',
  title: '',
  srOnlyLabel: '',
  dropdownDirection: Direction.BOTTOM_LEFT,
})

const { sessions } = useWebRTC()
const sessionRoomStore = useCallSessionRoomStore()
const { t } = useLocalization()
const { notifyError } = useRenderNotifier()
const isLoading = ref(false)

const resolveAcceptIcon = (loading: boolean): IconName => (
  loading || isLoading.value ? 'spinner' : props.acceptIcon
)

const resolveMenuIcon = (loading: boolean): IconName => (
  loading || isLoading.value ? 'spinner' : props.menuIcon
)

const room = computed(() => sessionRoomStore.getRoom(props.sessionId))
const incomingIsGroup = computed(() => !!room.value?.isGroup)

const hasConnectedGroupSession = computed(() => (
  getConnectedOtherSessions(props.sessionId, sessions.value).some(
    (facade) => !!sessionRoomStore.getRoom(facade.sessionId)?.isGroup,
  )
))

const showMenu = computed(() => (
  !props.hideAnswerMenu
  && shouldShowIncomingAnswerMenu(
    props.sessionId,
    sessions.value,
    incomingIsGroup.value,
    hasConnectedGroupSession.value,
  )
))

const defaultStrategy = computed<IncomingAnswerStrategy>(() => {
  // Модалка / без меню: просто answer — текущий уйдёт на hold в confirmed → holdAllExcept
  if (props.hideAnswerMenu) {
    return 'accept'
  }

  const hasConnected = getConnectedOtherSessions(props.sessionId, sessions.value).length > 0
  if (incomingIsGroup.value && hasConnected) {
    return 'end-and-accept'
  }
  return 'accept'
})

const onAnswer = async (
  answer: (withAudio: boolean, withVideo: boolean) => void | Promise<void>,
  strategy: IncomingAnswerStrategy,
  closeDropdown?: () => void,
) => {
  isLoading.value = true
  try {
    await runIncomingAnswerStrategy({
      answer,
      strategy,
      incomingSessionId: props.sessionId,
      media: {
        withAudio: props.withAudio,
        withVideo: props.withVideo,
      },
    })
    closeDropdown?.()
  } catch (error) {
    notifyError(error instanceof Error ? error.message : t('common.unknownError'))
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.incoming-answer-actions-menu__list :deep(.wui-list-item) {
  min-height: 56px;
}

.incoming-answer-actions-menu__item {
  transition: background-color .2s ease;
}

.incoming-answer-actions-menu__item:hover {
  background-color: var(--wui-bg-secondary);
}

.incoming-answer-actions-menu__item:active {
  background-color: var(--wui-bg-tertiary);
}

.incoming-answer-actions-menu__item :deep(.wui-icon) {
  color: var(--wui-text-main);
  height: 20px;
  width: 20px;
}
</style>
